import { useTranslation } from 'react-i18next';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

import { VisitFilterOperatorField } from './VisitFilterOperatorField';
import { VisitFilterTimeframeField } from './VisitFilterTimeframeField';
import { LastCompletedVisitFilterOperator, LastCompletedVisitFilterValue, PastTimeframeDays } from './_types';
import { VisitFilterSelectOption } from './constants';
import { useClientVisitFilterLabels } from './useClientVisitFilterLabels';
import { useSyncedVisitFilterDraft } from './useSyncedVisitFilterDraft';
import {
  getLastCompletedVisitFilterValidationErrorKey,
  isLastCompletedVisitFilterComplete,
} from './visitFilterValidation';

interface LastCompletedVisitFilterFieldsProps {
  value: LastCompletedVisitFilterValue | null | undefined;
  onChange: (value: LastCompletedVisitFilterValue | null) => void;
  timeframeOptions: VisitFilterSelectOption<number>[];
}

const isLastCompletedDraftEmpty = (draft: LastCompletedVisitFilterValue | null) =>
  !draft?.operator && !draft?.timeframeDays;

export const LastCompletedVisitFilterFields = ({
  value,
  onChange,
  timeframeOptions,
}: LastCompletedVisitFilterFieldsProps) => {
  const { getValidationError, lastCompletedOperators } = useClientVisitFilterLabels();
  const { t } = useTranslation('translation', { keyPrefix: 'clients.page.visitFilters', });
  const allowedTimeframeDays = timeframeOptions.map((option) => option.value);

  const { draft, patchDraft } =
    useSyncedVisitFilterDraft<LastCompletedVisitFilterValue>({
      value,
      onChange,
      isEmpty: isLastCompletedDraftEmpty,
      isComplete: (next) =>
        isLastCompletedVisitFilterComplete(next, allowedTimeframeDays),
    });

  const validationErrorKey = getLastCompletedVisitFilterValidationErrorKey(
    draft,
    allowedTimeframeDays
  );
  const validationError = validationErrorKey
    ? getValidationError(validationErrorKey)
    : null;
  const showErrors = Boolean(validationError);
  const isComplete = isLastCompletedVisitFilterComplete(
    draft,
    allowedTimeframeDays
  );

  const mergeDraft = (
    current: LastCompletedVisitFilterValue | null,
    patch: Partial<LastCompletedVisitFilterValue>
  ): LastCompletedVisitFilterValue => ({
    ...(current ?? {}),
    ...patch,
  });

  return (
    <Stack spacing={2}>
      <VisitFilterOperatorField
        value={draft?.operator}
        onChange={(operator) => patchDraft({ operator: operator as LastCompletedVisitFilterOperator }, mergeDraft)}
        operators={lastCompletedOperators}
        label={t('label.operator.lastCompleted')}
        error={showErrors && !draft?.operator}
      />
      <VisitFilterTimeframeField
        value={draft?.timeframeDays}
        options={timeframeOptions}
        onChange={(timeframeDays) =>
          patchDraft(
            { timeframeDays: timeframeDays as PastTimeframeDays },
            mergeDraft
          )
        }
        error={showErrors && !draft?.timeframeDays}
      />
      {validationError && !isComplete && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {validationError}
        </Typography>
      )}
    </Stack>
  );
};
